import { jwtDecode } from "jwt-decode";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  roles: string[];
  userId: string | null;
  login: (token: string) => void;
  logout: () => void;
}

interface JwtPayload {
  [key: string]: any;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  const navigate = useNavigate();

  const extractRoles = (decoded: JwtPayload): string[] => {
    const roleClaimKey =
      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
    const rawRoleClaim = decoded[roleClaimKey];

    if (Array.isArray(rawRoleClaim)) {
      return rawRoleClaim.filter((role): role is string => typeof role === "string");
    }

    if (typeof rawRoleClaim === "string") {
      return [rawRoleClaim];
    }

    return [];
  };

  const checkTokenAndRole = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token);
      const userRoles = extractRoles(decoded);
      const userId =
        decoded[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
        ];
      setIsAuthenticated(true);
      setRoles(userRoles);
      setIsSuperAdmin(userRoles.includes("SuperAdmin"));
      setIsAdmin(
        userRoles.includes("Admin") || userRoles.includes("SuperAdmin")
      );
      setUserId(userId);
    } else {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setRoles([]);
      setUserId(null);
    }
  }, []);

  useEffect(() => {
    checkTokenAndRole();
  }, [checkTokenAndRole]);

  const login = (token: string) => {
    localStorage.setItem("token", token);
    checkTokenAndRole();
    navigate("/");
  };

  const logout = () => {
    localStorage.removeItem("token");
    setIsAuthenticated(false);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setRoles([]);
    setUserId(null);
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAdmin,
        isSuperAdmin,
        roles,
        login,
        logout,
        userId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
